import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";

import { NeucleosWorldRail } from "./NeucleosWorldRail";
import { WorldBirthChooser } from "./WorldBirthChooser";
import { isElectron } from "~/env";
import { cn } from "~/lib/utils";
import { ALL_WORLDS_DOOR, LOCAL_WORLD_DOOR } from "~/neucleos/worldSelection";
import { useWorldThemeProjection } from "~/neucleos/worldThemeProjection";
import { usePrimarySettings } from "~/hooks/useSettings";
import {
  keepWorldInUrl,
  rememberLastDoor,
  rememberLastStance,
  rememberWorldRoom,
  refreshNeucleosWorlds,
  roomMemoryDoorKey,
  useNeucleosWorldStore,
} from "~/neucleos/worldStore";

type NeucleosFooterStatusKind = "quiet" | "signed-out";

export interface NeucleosFooterStatus {
  readonly kind: NeucleosFooterStatusKind;
  readonly label: string | null;
}

// A signed-in footer says nothing — the account line only appears when there
// is something to do: sign in. Checking/hydration also stays quiet so the
// login prompt never flashes at a member during the initial status read.
export function resolveNeucleosFooterStatus(input: {
  readonly loaded: boolean;
  readonly signedIn: boolean;
}): NeucleosFooterStatus {
  if (!input.loaded || input.signedIn) return { kind: "quiet", label: null };
  return { kind: "signed-out", label: "Log in to Neucleos" };
}

// FD-CAN-19 (2026-09-01): NeucleosStatusFooter retired with its bar — the
// login door it carried stands on DeskDock's bottom chip now, reading the
// same resolveNeucleosFooterStatus law and running the same sign-in
// ceremony. The resolver stays exported as that law's one seat.

export function NeucleosWorldFrame({ children }: { readonly children: ReactNode }) {
  const musicProvider = usePrimarySettings((settings) => settings.musicProvider);
  const pathname = useLocation({ select: (location) => location.pathname });
  const selection = useNeucleosWorldStore((state) => state.selection);
  const loaded = useNeucleosWorldStore((state) => state.loaded);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  // Local keeps its own room memory and its sentinel rides the URL so a
  // reload of a Local page stays Local; only World keys enter door memory.
  const doorKey = useNeucleosWorldStore((state) =>
    state.selection?.status === "local"
      ? LOCAL_WORLD_DOOR
      : state.selection?.status === "all"
        ? ALL_WORLDS_DOOR
        : state.activeWorldKey,
  );
  // Which slot the room memory writes to (WS9): every Local operating state —
  // signed-out and zero-Worlds included — owns the Local slot; a selected
  // World owns its key; nothing is remembered before hydration.
  const memoryDoorKey = useNeucleosWorldStore((state) => roomMemoryDoorKey(state));
  // The entered World's accent inputs ride the document root as
  // --ncl-world-accent-*; Local and refusals project nothing.
  useWorldThemeProjection(activeWorldKey);

  const roomOwner = useRef<{ door: string | null; pathname: string } | null>(null);

  useEffect(() => {
    void refreshNeucleosWorlds();
  }, []);

  useEffect(() => {
    if (doorKey) {
      keepWorldInUrl(doorKey);
      // World memory holds membership keys only (F03/W42): Local and ALL are
      // stances, entered by their explicit controls or ?world= sentinels — a
      // bare URL restores the last WORLD, else the leftmost membership.
      if (doorKey !== LOCAL_WORLD_DOOR && doorKey !== ALL_WORLDS_DOOR) rememberLastDoor(doorKey);
      // RM-CAN-14: the chosen stance is remembered apart from World memory, so a
      // relaunch (a bare URL) returns to Local when Local was the last choice.
      rememberLastStance(doorKey);
    }
    // A switch changes the World before the route (enterWorld), so for one
    // render the new World sits over the old World's page. That page belongs
    // to the World it was opened under; filing it under the new World makes
    // the new World refuse a session that was never its own (W-b26dffc3).
    // Remember only once the route has moved under this World, or when no
    // other World owned the page (first hydration).
    // The owner of a page is the World that was active when the route
    // arrived at it; it changes only when the route changes.
    if (roomOwner.current?.pathname !== pathname) {
      roomOwner.current = { door: memoryDoorKey, pathname };
    } else if (roomOwner.current.door === null) {
      roomOwner.current = { door: memoryDoorKey, pathname };
    }
    const pageBelongsToOtherDoor =
      memoryDoorKey !== null &&
      roomOwner.current.door !== null &&
      roomOwner.current.door !== memoryDoorKey;
    if (memoryDoorKey && !pageBelongsToOtherDoor) rememberWorldRoom(memoryDoorKey, pathname);
  }, [doorKey, memoryDoorKey, pathname]);

  // No chooser exists: selection means only which tab is active and the
  // resolver always lands somewhere. The one remaining non-room state — a
  // signed-in Actor whose selection cannot resolve to a membership — is a
  // typed note inside the normal shell, with the World rail still present
  // so the tabs remain the recovery.
  const unavailableMessage =
    loaded && signedIn && selection?.status === "unavailable" ? selection.message : null;

  return (
    <ShellStage
      header={
        <>
          {/* One Tab from anywhere to the work area: the frame's first
              focusable — it leads the titlebar row, BEFORE the rail's doors,
              so the keyboard law survives the rail's move above the shell
              (dated 2026-09-02). Visible only while focused; the target
              takes focus without ever becoming a Tab stop of its own
              (tabindex −1). */}
          <a
            className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:border focus:border-border focus:bg-background focus:px-2 focus:py-1 focus:text-xs focus:text-foreground"
            data-neucleos-skip-link=""
            href="#neucleos-world-view"
          >
            Skip to the World view
          </a>
          <NeucleosWorldRail />
        </>
      }
    >
      {/* The ALL stance's birth question, seated with the frame so every
          new-session surface can ask it (same seat law as the ceremony host). */}
      <WorldBirthChooser />
      {/* THE WORLD VIEW — canonical name (principal ruling 2026-09-03).
          Everything under the titlebar row: the one container that swaps
          wholesale when the World switcher (or ?world=, or a state-ticker
          line) selects a different World. One World, one World view; Local
          and ALL are stances that wear the same view. It holds the desk
          dock, the session families, the Cleo panel, the Canvas deck and the
          context rail. Superseded names, do not reuse: "world view",
          "canvasRevision". The id keeps the skip-link target stable. */}
      <div
        data-neucleos-world-view=""
        style={
          {
            "--music-provider-accent":
              musicProvider === "appleMusic"
                ? "var(--music-apple)"
                : musicProvider === "spotify"
                  ? "var(--music-spotify)"
                  : "var(--ncl-section-media)",
          } as CSSProperties
        }
        id="neucleos-world-view"
        tabIndex={-1}
        className="relative min-h-0 min-w-0 flex-1 overflow-hidden outline-none [transform:translateZ(0)] [&_[data-slot=sidebar-container]]:h-full! [&_[data-slot=sidebar-wrapper]]:h-full! [&_[data-slot=sidebar-wrapper]]:min-h-0!"
      >
        {unavailableMessage === null ? (
          children
        ) : (
          <div
            className="grid h-full place-items-center p-8 text-center text-sm text-muted-foreground"
            data-neucleos-world-unavailable=""
          >
            {unavailableMessage}
          </div>
        )}
      </div>
      {/* FD-CAN-19 (the principal's word: "then we can remove the footer.
          matching the design!"): both footer bars retired — the transport
          lives in the context dock's music seat, and the login door stands
          on the left dock's bottom chip. */}
    </ShellStage>
  );
}

/**
 * FD5 (register FD-CAN-05; principal words 2026-09-01: "I do want the
 * outter space around"): the design's atmosphere — the whole Desk floats as
 * one inset rounded shell on a dark outer ground, with a soft ring of the
 * section accents glowing behind it and a radial glass gradient inside it.
 *
 * Every color is a contract token (D-FD4): the ground is the theme canvas,
 * the ring is a conic gradient of the `--ncl-aurora-N` spectrum (FD31, dated; emitted by
 * the palette applier in every branch), and the shell ground reads the
 * theme's surface/toolbar/chrome roles — so Founders reproduces the design
 * of record exactly while every other theme keeps a coherent shell. The
 * ring hides under forced colors. Exported pure so the story renders it
 * whole.
 *
 * DATED 2026-09-02 (the principal: move the logo and the switcher up into
 * the titlebar band and remove the empty row): the stage seats a `header`
 * — the World rail — ABOVE the shell, so the app header and the window's
 * titlebar are one row (--desk-header-height, the main process's
 * TITLEBAR_HEIGHT). The header rides the aurora directly; the shell begins
 * beneath it, its corners tucking under the row like a card. In the
 * desktop app the whole stage is the drag surface — the shell opts out
 * with no-drag, so the app-region reaches the header row and the gutters,
 * never the shell's children — and the rail's own drag duty is the same
 * region. The browser wears the identical row, minus the native-control
 * insets.
 *
 * Named absence: Electron's launch backgroundColor still predates the
 * shell ground.
 */
/**
 * FD31 (register FD-CAN-31): the aurora's stops. The design of record's
 * ring runs eight hues at .5/.5/.45/.5/.45/.55/.5/.5 alpha and closes on
 * its first; each stop here mixes its `--ncl-aurora-N` toward TRANSPARENT
 * at edge-light plus that stop's dated offset (the design's alpha minus
 * .5, in percent). Exported so the story can count the law.
 */
export const AURORA_STOP_OFFSETS: readonly number[] = Object.freeze([0, 0, -5, 0, -5, 5, 0, 0]);

// Written out stop by stop (not generated) so the source itself is the
// law the oracles read: nine reads of --ncl-aurora-N, nine mixes toward
// transparent, nine reads of edge-light.
export const AURORA_RING_BACKGROUND =
  "conic-gradient(from var(--ncl-effect-aurora-start) at 50% 50%, " +
  "color-mix(in oklab, var(--ncl-aurora-1) calc(var(--ncl-effect-edge-light) + 0%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-2) calc(var(--ncl-effect-edge-light) + 0%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-3) calc(var(--ncl-effect-edge-light) - 5%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-4) calc(var(--ncl-effect-edge-light) + 0%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-5) calc(var(--ncl-effect-edge-light) - 5%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-6) calc(var(--ncl-effect-edge-light) + 5%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-7) calc(var(--ncl-effect-edge-light) + 0%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-8) calc(var(--ncl-effect-edge-light) + 0%), transparent), " +
  "color-mix(in oklab, var(--ncl-aurora-1) calc(var(--ncl-effect-edge-light) + 0%), transparent))";

export function ShellStage({
  header,
  children,
}: {
  /** The titlebar row seated above the shell (the World rail). Absent: the frame's 14px top gutter. */
  readonly header?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div
      className={cn(
        // DATED 2026-09-07 (the principal's review): overflow-clip, never
        // hidden — a hidden box is still a scroll container, and the ring's
        // 140px overreach made the stage scrollable, so any focus below the
        // fold scrolled the titlebar out of the window with no way back.
        "relative flex h-dvh min-h-0 w-full min-w-0 flex-col overflow-clip px-[14px] pb-[14px]",
        header === undefined && "pt-[14px]",
        isElectron && "drag-region",
      )}
      data-cleo-shell-stage=""
      // FD31: the outer ground is the theme's CANVAS, read directly — the
      // shadcn --background it wore before resolved to the neutral dark
      // ground on the live Desk (the :root dark variant outranked the
      // theme remap), so the void was grey under a navy shell.
      style={{ background: "var(--app-theme-canvas, var(--ncl-canvas))" }}
    >
      {/* FD23 (register FD-CAN-23): the living aurora. Black is not a
          color here — every stop mixes its hue toward TRANSPARENT at the
          contract's edge-light level (per-stop offsets breathing the
          luminance around the ring), so the ground shows through: today's
          dark, a future light theme's light. The drift is transform-only
          (shell-drift: rotate + scale breathe, the design of record's 60s
          cadence) so the blurred layer rasterizes once; motion-safe stills
          it under reduced motion. Standing performance law: animated color
          never runs under frost — the opaque shell below keeps covering
          this ring everywhere but the 14px gutter, so no backdrop-filter
          re-rasterizes per frame.
          FD31: the stops are the aurora's own eight hues (--ncl-aurora-N,
          the design's spectrum; the section family lacked its red, orange,
          yellow, and teal), the blur and start angle are contract tokens,
          and no second opacity dims the ring — edge-light is the one
          intensity. The design's 140px overreach keeps the blur from
          fading before the gutter. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-[140px] forced-colors:hidden motion-safe:animate-[shell-drift_60s_linear_infinite]"
        data-cleo-shell-ring=""
        style={{
          background: AURORA_RING_BACKGROUND,
          filter: "blur(var(--ncl-effect-aurora-blur))",
        }}
      />
      {/* The titlebar row: the header stands on the aurora itself, painted
          above the ring (a positioned later sibling) and before the shell. */}
      {header !== undefined ? (
        <div className="relative w-full min-w-0 flex-none" data-cleo-shell-header="">
          {header}
        </div>
      ) : null}
      {/* FD31: the shell's two edges from the design — a 1px inner ring
          (the theme's text at the shell-ring share, so a light theme's
          ring rises out of light) and the inner vignette that lets the
          void read as depth rather than flat paint. No border utility:
          the ring is the box-shadow so it never adds to the box. */}
      <div
        className={cn(
          "relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-[26px]",
          isElectron && "[-webkit-app-region:no-drag]",
        )}
        data-cleo-shell=""
        style={{
          background:
            "radial-gradient(120% 90% at 50% 0%, var(--app-theme-surface, var(--ncl-surface)), var(--app-theme-toolbar, var(--ncl-toolbar)) 55%, var(--app-theme-chrome, var(--ncl-chrome)))",
          boxShadow:
            "inset 0 0 0 1px color-mix(in oklab, var(--app-theme-text, var(--ncl-text)) var(--ncl-effect-shell-ring), transparent), " +
            "inset 0 0 var(--ncl-effect-shell-vignette) color-mix(in oklab, var(--app-theme-chrome, var(--ncl-chrome)) 90%, transparent)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
